import { useEffect } from 'react'
import type { dia } from '@joint/core'
import { useFloorStore } from '../../../map-editor-shared/store/floorStore'
import { useCsvRuntimeStore } from '../../../map-editor-features/csv/model/csvStore'

interface UseProjectStateResetProps {
  currentLot: string | null
  graph: dia.Graph | null
  previousLotRef: React.MutableRefObject<string | null>
  setCurrentFloor: (floor: string | null) => void
  setElementCount: (count: number) => void
  setObjectsByLayer: (objectsByLayer: Map<string, dia.Element[]>) => void
  setLoadedFileName: (fileName: string | null) => void
  setSelectedElementId: (id: string | null) => void
  setSelectedElementIds: (ids: Set<string>) => void
  setSelectedTypeId: (id: string | null) => void
  setPendingGraphJson: (json: any | null) => void
}

/**
 * Hook to reset all state when switching projects.
 * Clears canvas state, CSV state, and resets selection when currentLot changes.
 */
export function useProjectStateReset({
  currentLot,
  graph,
  previousLotRef,
  setCurrentFloor,
  setElementCount,
  setObjectsByLayer,
  setLoadedFileName,
  setSelectedElementId,
  setSelectedElementIds,
  setSelectedTypeId,
  setPendingGraphJson,
}: UseProjectStateResetProps): void {
  useEffect(() => {
    const isInitialMount = previousLotRef.current === null
    const isProjectChange = previousLotRef.current !== currentLot

    if (!isInitialMount && !isProjectChange) return

    // On actual project switch (not initial mount): wipe previous project's canvas/state
    if (isProjectChange && !isInitialMount) {
      if (graph) {
        graph.clear()
      }

      setElementCount(0)
      setObjectsByLayer(new Map())
      setLoadedFileName(null)
      setSelectedElementId(null)
      setSelectedElementIds(new Set())
      setSelectedTypeId(null)
      setPendingGraphJson(null)

      setCurrentFloor(null)

      // Delete stale floors for the lot we are *leaving* so useFloorSelection
      // recreates them from API data when it runs for the new lot.
      const prevLot = previousLotRef.current
      if (prevLot) {
        const floorState = useFloorStore.getState()
        const oldFloors = floorState.floors.filter(
          f => String(f.lotId) === String(prevLot)
        )
        oldFloors.forEach(floor => {
          floorState.deleteFloor(floor.id)
        })
      }
    }

    // On initial mount: clean up stale floors from OTHER projects left in localStorage.
    // When EditorPage re-mounts (e.g., user navigated away and back), localStorage may
    // contain floors from a previous project. Without cleanup, useFloorSelection can
    // accidentally match old floors by name/order and skip creating correct ones.
    if (isInitialMount && currentLot) {
      const floorState = useFloorStore.getState()
      const staleFloors = floorState.floors.filter(
        f => String(f.lotId) !== String(currentLot)
      )
      staleFloors.forEach(floor => {
        floorState.deleteFloor(floor.id)
      })
    }

    // CSV runtimes always cleared (initial mount + project switch)
    useCsvRuntimeStore.getState().clearAllCsvRuntime()

    previousLotRef.current = currentLot
  }, [
    currentLot,
    graph,
    setCurrentFloor,
    setElementCount,
    setObjectsByLayer,
    setLoadedFileName,
    setSelectedElementId,
    setSelectedElementIds,
    setSelectedTypeId,
    setPendingGraphJson,
    previousLotRef,
  ])
}
